import React, { useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import { useFormContext } from 'react-hook-form';
import { urlToList } from 'utils';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';

import BWButton from '../BWButton';
import PropTypes from 'prop-types';
import routes from 'routers';
import CheckAccess from 'navigation/CheckAccess';
import styled from 'styled-components';

const Footer = styled.div``;
const noop = () => {};

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const FormSection = ({
  loading = false,
  detailForm = [],
  onSubmit = noop,
  disabled = false,
  actions,
  noActions = false,
  noSideBar,
  noPadding,
  style,
  customerClose,
  hideClose = false,
  stopSubmitPropagation = false,
  onUserInteraction,
  disabledBtn,
  requireAssigned = false,
  onInvalid,
}) => {
  const methods = useFormContext();
  const {
    formState: { isSubmitting },
  } = methods;
  const { pathname } = useLocation();
  // Find name function
  const findNameFunctions = useMemo(() => pathname?.split('/')?.[1], [pathname]);
  const PERMISSION = useMemo(() => {
    const functionsRouteGroup = routes.filter((o) => o?.path.includes(`/${findNameFunctions}/`));
    return {
      edit: functionsRouteGroup?.find((o) => o?.path.includes('/edit'))?.function,
      add: functionsRouteGroup?.find((o) => o?.path.includes('/add'))?.function,
      detail: functionsRouteGroup?.find((o) => o?.path.includes('/detail'))?.function,
    };
  }, [findNameFunctions]);

  const params = useParams();

  const loadingForm = loading || isSubmitting;

  const id = useMemo(() => {
    return params[`${findNameFunctions}_id`] ?? params?.id ?? params[Object.keys(params).find((p) => p.includes('id'))];
  }, [params, findNameFunctions]);

  const isView = useMemo(() => pathname.includes('/detail') || pathname.includes('/view'), [pathname]);
  const isAdd = useMemo(() => pathname.includes('/add'), [pathname]);
  const isEdit = useMemo(() => pathname.includes('/edit'), [pathname]);
  const history = useHistory();
  const path = urlToList(useLocation().pathname)[0];

  const goToPreviousPath = useCallback(() => {
    history.push(`${path}`);
  }, [history, path]);
  const goToEditPath = useCallback(
    (e) => {
      e.preventDefault();
      history.push(`${path}/edit/${id}`);
    },
    [history, id, path],
  );

  const jsx_footer = useMemo(() => {
    if (noActions) {
      return;
    }

    if (React.isValidElement(actions)) {
      return actions;
    } else if (Array.isArray(actions)) {
      return actions?.filter((p) => !p.hidden).map((props, i) => <BWButton className='mr-2' key={i} {...props} />);
    } else {
      if (isView) {
        return (
          <CheckAccess permission={PERMISSION?.edit} requireAssigned={requireAssigned}>
            <BWButton
              type='primary'
              outline
              icon='fi fi-rr-edit'
              content='Chỉnh sửa'
              onClick={goToEditPath}
              disabled={disabledBtn}
            />
          </CheckAccess>
        );
      } else if (isAdd || isEdit) {
        return (
          <CheckAccess
            permission={isEdit ? PERMISSION?.edit : PERMISSION?.add}
            requireAssigned={isEdit && requireAssigned}>
            <BWButton
              submit
              type='primary'
              icon='fi fi-rr-check'
              content={`Hoàn tất ${isEdit ? 'chỉnh sửa' : 'thêm mới'}`}
              disabled={isEdit && disabledBtn}
            />
          </CheckAccess>
        );
      }
    }
  }, [
    PERMISSION?.add,
    PERMISSION?.edit,
    actions,
    disabledBtn,
    goToEditPath,
    isAdd,
    isEdit,
    isView,
    noActions,
    requireAssigned,
  ]);

  const jsx_close = useMemo(() => {
    if (!noActions && !hideClose) {
      return (
        <BWButton
          type='primary'
          outline
          icon='fi fi-rr-cross-small'
          content='Đóng'
          onClick={customerClose ?? goToPreviousPath}
        />
      );
    }
  }, [customerClose, goToPreviousPath, hideClose, noActions]);

  const parseDetailForm = useMemo(() => {
    return (detailForm ?? []).filter((p) => !p.hidden) ?? [];
  }, [detailForm]);

  const jsx_main = (
    <React.Fragment>
      <div style={style} className='bw_main_wrapp'>
        <div className='bw_row'>
          <div className={`bw_col_12 ${noPadding ? '' : 'bw_pb_6'}`}>
            <Spin spinning={loadingForm} indicator={antIcon}>
              <div className='bw_row'>
                {parseDetailForm.map((p, key) => {
                  const { title, id, key: componentKey, component: Component, className, ...props } = p;
                  return (
                    <div
                      key={componentKey || id || key}
                      id={`bw_form_section_${key}`}
                      className={className ?? 'bw_col_12'}>
                      <Component id={id} title={title} disabled={disabled} {...props} />
                    </div>
                  );
                })}
              </div>
            </Spin>
          </div>
        </div>
      </div>
      {!noActions && !isSubmitting && (
        <Footer className='bw_form_section_actions bw_btn_save bw_btn_group bw_flex bw_justify_content_right bw_align_items_center'>
          {jsx_footer}
          {jsx_close}
        </Footer>
      )}
    </React.Fragment>
  );

  return noActions ? jsx_main : <form onSubmit={(event) => {
    if (stopSubmitPropagation) event.stopPropagation();
    return methods.handleSubmit(onSubmit, onInvalid)(event);
  }}
    onChangeCapture={onUserInteraction} onInputCapture={onUserInteraction}
    onClickCapture={onUserInteraction} onKeyDownCapture={onUserInteraction}>{jsx_main}</form>;
};

FormSection.propTypes = {
  loading: PropTypes.bool,
  detailForm: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      component: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),
      fieldActive: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  onSubmit: PropTypes.func,
  onInvalid: PropTypes.func,
  disabled: PropTypes.bool,
  disabledBtn: PropTypes.bool,
  requireAssigned: PropTypes.bool,

  noActions: PropTypes.bool,
  hideClose: PropTypes.bool,
  stopSubmitPropagation: PropTypes.bool,
  onUserInteraction: PropTypes.func,
  actions: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(
      PropTypes.shape({
        className: PropTypes.string,
        type: PropTypes.oneOf(['primary', 'danger', 'success', 'warning', 'blue', 'red', 'green']),
        icon: PropTypes.string,
        content: PropTypes.string,
        disabled: PropTypes.bool,
        onClick: PropTypes.func,
        loading: PropTypes.bool,
        outline: PropTypes.bool,
      }),
    ),
  ]),
};

export default FormSection;
